import { createColumnHelper } from "@tanstack/react-table";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import cmsClient from "src/services/cmsClient";
import { adaptRegionReport } from "src/features/reporting/adapters";
import StoreObservationTableCell from "../../components/StoreObservationTableCell/StoreObservationTableCell";
import IndividualObservationTableCell from "../../components/IndividualObservationTableCell/IndividualObservationTableCell";
import GreatConversationTableCell from "../../components/GreatConversationTableCell/GreatConversationTableCell";

interface RegionReportOptions {
	year?: number;
	formId: number;
	compliancy?: boolean;
	threshold?: boolean;
	observationType: IObservationType;
}

const columnHelper = createColumnHelper<any>();

const downloadRegionReport = async (
	form: number,
	type: "complete" | "compliant" | "threshold",
	year?: number
) => {
	const response = await cmsClient.get<any>(
		"/observations/reports/region-by-month/csv/",
		{ params: { year, form, type, includeIgnored: true }, responseType: "blob" }
	);

	return response.data;
};

const fetchRegionReport = async (
	formId: number,
	observationType: IObservationType,
	year?: number
) => {
	const response = await cmsClient.get<IRegionReport[]>(
		"/observations/reports/region-by-month/",
		{
			params: {
				year,
				form: formId,
				includeIgnored: true
			}
		}
	);

	return response.data;
};

const useRegionReport = ({
	formId,
	compliancy,
	threshold,
	observationType,
	year
}: RegionReportOptions) => {
	const queryKey = ["region-report", { formId, year }];
	const queryFn = () => fetchRegionReport(formId, observationType, year);

	const query = useQuery({ queryKey, queryFn, select: adaptRegionReport });

	const months = Array.from({ length: 12 }, (_, i) => i).map((month) =>
		dayjs().month(month).format("MMM")
	);

	const monthlyColumns = months.map((month) => {
		return columnHelper.accessor(month, {
			cell: ({ cell, row }) => {
				if (observationType === "INDIVIDUAL") {
					return <IndividualObservationTableCell cellValue={cell.getValue()} />;
				}

				if (observationType === "GREAT_CONVERSATION") {
					return <GreatConversationTableCell cellValue={cell.getValue()} />;
				}

				return (
					<StoreObservationTableCell
						cellValue={cell.getValue()}
						showStatistics={true}
						regionId={row.original.regionId}
					/>
				);
			},
			header: month
		});
	});

	const columns = [
		columnHelper.display({
			id: "region-name",
			cell: ({ row }) => row.original.name,
			header: "Region"
		}),
		...monthlyColumns
	];

	const download = async () => {
		const response = await downloadRegionReport(
			formId,
			compliancy ? "compliant" : threshold ? "threshold" : "complete",
			year
		);

		const url = window.URL.createObjectURL(new Blob([response]));

		const link = document.createElement("a");
		link.href = url;
		link.setAttribute("download", "region-by-month.csv");

		document.body.appendChild(link);

		link.click();
	};

	return { columns, download, ...query };
};

export default useRegionReport;
